import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useDashboardAnalytics } from "@/hooks/useDashboardAnalytics";
import { useSalesGoals } from "@/hooks/useSalesGoals";
import { ShoppingBag, DollarSign, Package, AlertTriangle, TrendingUp, TrendingDown, Receipt, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { SalesGoalCard } from "@/components/dashboard/SalesGoalCard";
import { HourlySalesChart } from "@/components/dashboard/HourlySalesChart";
import { WeeklyTrendChart } from "@/components/dashboard/WeeklyTrendChart";
import { PrinterStatusCard } from "@/components/dashboard/PrinterStatusCard";
import { useNavigate } from "react-router-dom";

export default function DashboardHome() {
  const { activeRestaurant } = useRestaurantContext();
  const analytics = useDashboardAnalytics();
  const { setGoal } = useSalesGoals();
  const navigate = useNavigate();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleSetGoal = async (amount: number) => {
    const now = new Date();
    return await setGoal(now.getMonth() + 1, now.getFullYear(), amount);
  };

  const renderGrowthIndicator = (value: number) => {
    if (value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`flex items-center text-xs ${isPositive ? "text-success" : "text-destructive"}`}>
        {isPositive ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Olá, bem-vindo!
        </h1>
        <p className="text-muted-foreground mt-1">
          Gerencie {activeRestaurant?.name || "seu restaurante"} de forma simples e eficiente.
        </p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Vendas Hoje */}
        <Card className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Vendas Hoje
            </CardTitle>
            <div className="h-9 w-9 rounded-lg bg-success/10 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-success" />
            </div>
          </CardHeader>
          <CardContent>
            {analytics.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">
                  {formatCurrency(analytics.salesToday)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.ordersToday} pedidos • Ticket médio: {formatCurrency(analytics.averageTicketToday)}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Vendas do Mês */}
        <Card className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Vendas do Mês
            </CardTitle>
            <div className="h-9 w-9 rounded-lg bg-brand/10 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-brand" />
            </div>
          </CardHeader>
          <CardContent>
            {analytics.isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-foreground">
                    {formatCurrency(analytics.salesThisMonth)}
                  </span>
                  {renderGrowthIndicator(analytics.salesGrowthPercent)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.ordersThisMonth} pedidos • vs. mês anterior
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Produtos Ativos */}
        <Card className="bg-card border-border hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Produtos Ativos
            </CardTitle>
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Package className="h-5 w-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            {analytics.isLoading ? (
              <Skeleton className="h-8 w-12" />
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">
                  {analytics.activeProducts}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.activeCategories} categorias ativas
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Estoque Baixo */}
        <Card 
          className={`border-border hover:shadow-md transition-shadow cursor-pointer ${analytics.lowStockItems > 0 ? "bg-warning/5 border-warning/30" : "bg-card"}`}
          onClick={() => navigate('/dashboard/estoque')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Estoque Baixo
            </CardTitle>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${analytics.lowStockItems > 0 ? "bg-warning/10" : "bg-muted"}`}>
              <AlertTriangle className={`h-5 w-5 ${analytics.lowStockItems > 0 ? "text-warning" : "text-muted-foreground"}`} />
            </div>
          </CardHeader>
          <CardContent>
            {analytics.isLoading ? (
              <Skeleton className="h-8 w-12" />
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">
                  {analytics.lowStockItems}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.lowStockItems === 0 ? "Estoque em dia" : "itens abaixo do mínimo"}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SalesGoalCard
          currentSales={analytics.salesThisMonth}
          goalAmount={analytics.monthlyGoal}
          progressPercent={analytics.goalProgress}
          onSetGoal={handleSetGoal}
        />
        <HourlySalesChart
          data={analytics.hourlySales}
          isLoading={analytics.isLoading}
        />
        <WeeklyTrendChart
          data={analytics.last7DaysTrend}
          isLoading={analytics.isLoading}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PrinterStatusCard />
        
        <Card className="bg-gradient-to-br from-brand/5 to-brand/10 border-brand/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-brand" />
              Comece Agora
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-1">
              <li>Adicione categorias ao seu cardápio</li>
              <li>Cadastre seus produtos</li>
              <li>Inicie as vendas no PDV</li>
              <li>Acompanhe os relatórios</li>
            </ol>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-secondary to-muted border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Precisa de Ajuda?
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <p>
              Entre em contato com nosso suporte pelo WhatsApp ou acesse a documentação 
              para tirar suas dúvidas.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
